import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, type Profile, type UserRole } from "@/lib/supabase";
import { useWeeklyStreak } from "@/hooks/useWeeklyStreak";

const roleColors: Record<UserRole, string> = {
  student: "bg-amber-100 text-amber-700",
  teacher: "bg-emerald-100 text-emerald-700",
  parent: "bg-rose-100 text-rose-700",
};

const SUBJECT_OPTIONS = ["SAT Math", "SAT Reading", "ACT Prep", "AP Physics", "AP Chemistry", "AP Biology", "Calculus", "Algebra", "Essay Writing", "College Applications", "English Literature", "History", "Economics", "Computer Science"];

export default function ProfilePage() {
  useWeeklyStreak();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<Partial<Profile>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "docs" | "contact">("info");
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docs, setDocs] = useState<{ name: string; url: string; type: string; uploaded_at: string }[]>([]);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) { navigate("/auth"); return; }
      const { data: p } = await supabase.from("profiles").select("*").eq("id", data.user.id).maybeSingle();
      if (p) {
        setProfile(p as Profile);
        setSelectedSubjects(p.subjects ? p.subjects.split(",").map((s: string) => s.trim()).filter(Boolean) : []);
        setDocs(Array.isArray(p.documents) ? p.documents : []);
      } else {
        setProfile({ id: data.user.id, role: "student", score: 0, weekly_streak: 0 });
      }
      setLoading(false);
    });
  }, [navigate]);

  const handleSave = async () => {
    if (!profile.id) return;
    setSaving(true);
    const updateData = {
      full_name: profile.full_name,
      bio: profile.bio,
      looking_for: profile.looking_for,
      wechat_id: profile.wechat_id,
      whatsapp_id: profile.whatsapp_id,
      subjects: selectedSubjects.join(", "),
      grade: profile.grade,
      region: profile.region,
      documents: docs,
    };
    await supabase.from("profiles").update(updateData).eq("id", profile.id);
    setSaving(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const toggleSubject = (s: string) => {
    setSelectedSubjects((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile.id) return;
    setUploadingDoc(true);
    const filePath = `${profile.id}/${Date.now()}_${file.name}`;
    const { data, error } = await supabase.storage.from("documents").upload(filePath, file);
    if (!error && data) {
      const { data: urlData } = supabase.storage.from("documents").getPublicUrl(filePath);
      const newDoc = { name: file.name, url: urlData.publicUrl, type: file.type, uploaded_at: new Date().toISOString() };
      const updatedDocs = [...docs, newDoc];
      setDocs(updatedDocs);
      await supabase.from("profiles").update({ documents: updatedDocs }).eq("id", profile.id);
    }
    setUploadingDoc(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeDoc = async (idx: number) => {
    if (!profile.id) return;
    const updated = docs.filter((_, i) => i !== idx);
    setDocs(updated);
    await supabase.from("profiles").update({ documents: updated }).eq("id", profile.id);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-amber-500"></i>
          <p className="text-gray-400 text-sm">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const displayName = profile.full_name || profile.name || "Your Profile";

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navbar */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500">
              <i className="ri-graduation-cap-fill text-white"></i>
            </div>
            <span className="font-bold text-gray-900 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Crown SAT &amp; University</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/community" className="text-sm text-gray-500 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">Browse Community</Link>
            <button onClick={handleSignOut} className="px-4 py-2 rounded-full border border-gray-200 text-gray-600 text-sm font-semibold hover:border-red-300 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap">Sign Out</button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Profile header card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-amber-100 flex items-center justify-center flex-shrink-0">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover object-top" />
              ) : (
                <i className="ri-user-3-line text-3xl text-amber-400"></i>
              )}
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap mb-1">
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>{displayName}</h1>
              {profile.role && <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${roleColors[profile.role as UserRole]}`}>{profile.role}</span>}
            </div>
            {profile.region && <p className="text-gray-400 text-sm flex items-center gap-1"><i className="ri-map-pin-line"></i>{profile.region}</p>}
          </div>
          {/* Score & streak */}
          <div className="flex gap-4 flex-shrink-0">
            <div className="text-center bg-emerald-50 rounded-xl px-4 py-3">
              <p className="text-2xl font-bold text-emerald-600">{profile.score || 0}</p>
              <p className="text-xs text-gray-400">Score pts</p>
            </div>
            <div className="text-center bg-amber-50 rounded-xl px-4 py-3">
              <p className="text-2xl font-bold text-amber-500">🔥 {profile.weekly_streak || 0}</p>
              <p className="text-xs text-gray-400">Week streak</p>
            </div>
          </div>
        </div>

        {/* Weekly streak info */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-400 rounded-2xl p-4 mb-6 flex items-center gap-4 text-white">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
            <i className="ri-fire-line text-xl"></i>
          </div>
          <div>
            <p className="font-semibold text-sm">Weekly Login Streak Active!</p>
            <p className="text-white/80 text-xs">You earn +10 pts every week you log in. Every 4 weeks = +80 bonus pts. Keep it up!</p>
          </div>
          <div className="ml-auto flex-shrink-0 text-right">
            <p className="text-2xl font-bold">{profile.weekly_streak || 0}</p>
            <p className="text-white/70 text-xs">weeks</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-stone-100 rounded-full p-1 mb-6 w-fit">
          {([["info", "My Info"], ["docs", "Documents"], ["contact", "Contact Info"]] as [typeof activeTab, string][]).map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>{label}</button>
          ))}
        </div>

        {saveSuccess && (
          <div className="mb-4 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-emerald-500"></i>
            <p className="text-emerald-700 text-sm font-medium">Profile saved successfully!</p>
          </div>
        )}

        {/* Info Tab */}
        {activeTab === "info" && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name</label>
                <input type="text" value={profile.full_name || ""} onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))} placeholder="Your full name" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Region / Location</label>
                <input type="text" value={profile.region || ""} onChange={(e) => setProfile((p) => ({ ...p, region: e.target.value }))} placeholder="e.g. New York, USA" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
              {profile.role === "student" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Grade</label>
                  <select value={profile.grade || ""} onChange={(e) => setProfile((p) => ({ ...p, grade: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors bg-white">
                    <option value="">Select grade</option>
                    {["Grade 6","Grade 7","Grade 8","Grade 9","Grade 10","Grade 11","Grade 12"].map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">About Me</label>
              <textarea value={profile.bio || ""} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} placeholder="Tell others about yourself, your background, and your goals..." rows={4} maxLength={500} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none" />
              <p className="text-xs text-gray-300 mt-1 text-right">{(profile.bio || "").length}/500</p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">What I Need Help With / What I Offer</label>
              <textarea value={profile.looking_for || ""} onChange={(e) => setProfile((p) => ({ ...p, looking_for: e.target.value }))} placeholder="e.g. I'm looking for an SAT Math tutor who can help me reach 800..." rows={3} maxLength={300} className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Subjects</label>
              <div className="flex flex-wrap gap-2">
                {SUBJECT_OPTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => toggleSubject(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${selectedSubjects.includes(s) ? "bg-amber-500 border-amber-500 text-white" : "border-gray-200 text-gray-600 hover:border-amber-300"}`}>{s}</button>
                ))}
              </div>
            </div>

            <button onClick={handleSave} disabled={saving} className="self-start px-8 py-3 rounded-full bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Saving...</span> : "Save Changes"}
            </button>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "docs" && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-gray-800">My Documents</h3>
                <p className="text-gray-400 text-sm">Upload transcripts, essays, certificates, or any relevant documents.</p>
              </div>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {uploadingDoc ? <><i className="ri-loader-4-line animate-spin"></i>Uploading...</> : <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-line"></i></div>Upload File</>}
              </button>
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleFileUpload} className="hidden" />
            </div>

            {docs.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-100 mx-auto mb-3">
                  <i className="ri-file-upload-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 font-medium text-sm">No documents yet</p>
                <p className="text-gray-400 text-xs mt-1">Upload transcripts, essays, or certificates to share with your matches</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {docs.map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 bg-stone-50 rounded-xl p-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0">
                      <i className={`${doc.type.includes("pdf") ? "ri-file-pdf-line text-red-500" : doc.type.includes("image") ? "ri-image-line text-blue-500" : "ri-file-text-line text-amber-500"} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-sm truncate">{doc.name}</p>
                      <p className="text-gray-400 text-xs">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a href={doc.url} target="_blank" rel="nofollow noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-amber-500 hover:border-amber-300 transition-colors cursor-pointer">
                        <i className="ri-external-link-line text-sm"></i>
                      </a>
                      <button onClick={() => removeDoc(i)} className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-300 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === "contact" && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-5">
            <p className="text-gray-400 text-sm">Your contact info is only revealed to people you choose to connect with.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-whatsapp-line text-emerald-500"></i></div>
                  WhatsApp Number
                </label>
                <input type="text" value={(profile as Profile & { whatsapp_id?: string }).whatsapp_id || ""} onChange={(e) => setProfile((p) => ({ ...p, whatsapp_id: e.target.value }))} placeholder="+1 555 000 0000" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-emerald-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-wechat-line text-emerald-600"></i></div>
                  WeChat ID
                </label>
                <input type="text" value={profile.wechat_id || ""} onChange={(e) => setProfile((p) => ({ ...p, wechat_id: e.target.value }))} placeholder="your_wechat_id" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-emerald-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-amber-500"></i></div>
                  Contact Email
                </label>
                <input type="email" name="email" value={profile.email || ""} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} placeholder="you@example.com" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-shield-check-line text-amber-500"></i>
              </div>
              <p className="text-amber-700 text-xs leading-relaxed">Your contact details are private by default. They are only shown when you click &ldquo;Connect&rdquo; on someone&apos;s profile and choose to reveal them.</p>
            </div>
            <button onClick={handleSave} disabled={saving} className="self-start px-8 py-3 rounded-full bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Saving...</span> : "Save Contact Info"}
            </button>
          </div>
        )}
      </div>

      <footer className="bg-stone-900 text-white py-8 mt-6">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs">&copy; 2026 Crown SAT &amp; University · www.CrownSATandUniversity.com</p>
          <Link to="/" className="text-white/40 text-xs hover:text-white/70 transition-colors cursor-pointer">← Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}
